/**
 * §14.8 screen 11 — Tarix, the audit log made visible.
 *
 * Capability `audit.read` (`admin` only), org-wide. Read-only by construction:
 * there is no write, edit or delete route for `audit_log`, so this screen has
 * no action of any kind — not a delete, not an "acknowledge", not a note field.
 * A log its subjects can edit is not a log.
 *
 * Three rendering rules, each from §14.6:
 *
 *  1. **`revealed` is a BADGE on the row**, not a key inside an expanded blob.
 *     It is the marker that separates a handler opening a case from a handler
 *     deliberately unmasking a citizen's phone number — the single most
 *     important line in this log, and buried behind an expander it is a fact
 *     nobody would ever look for.
 *  2. **An unknown action renders its RAW string.** The label set is closed
 *     (`AUDIT_ACTIONS`), and a route added later without a label must show as
 *     `murojaat.something.new` rather than as a blank cell. The panel falls
 *     back to the raw value, and the row is still complete.
 *  3. **No free-text search over `detail`.** The filters are actor, action,
 *     subject and date range. `detail` is already PII-free by the write-time
 *     allow-list, but a search box over it would invite someone to try a
 *     citizen's name in it and would make the log's shape look negotiable.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { ACTOR_KINDS, AUDIT_ACTIONS, isAnyReference } from '@navoiy/domain';
import { ApiError, api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDateTime, fromDateInput } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
} from '../ui/index.jsx';

type AuditEntry = components['schemas']['AdminAuditEntry'];

interface Filters {
  actor_kind: string;
  action: string;
  subject_id: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { actor_kind: '', action: '', subject_id: '', from: '', to: '' };
const PAGE_SIZE = 50;

export function AuditScreen(): React.JSX.Element {
  const { t } = useI18n();
  const { expire } = useSession();

  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (from: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const fromAt = fromDateInput(applied.from);
        const toAt = fromDateInput(applied.to, true);
        const data = unwrap(
          await api().GET('/admin/audit', {
            params: {
              query: {
                limit: PAGE_SIZE,
                ...(applied.actor_kind ? { actor_kind: applied.actor_kind } : {}),
                ...(applied.action ? { action: applied.action } : {}),
                ...(applied.subject_id ? { subject_id: applied.subject_id.toUpperCase() } : {}),
                ...(fromAt ? { from: fromAt } : {}),
                ...(toAt ? { to: toAt } : {}),
                ...(from ? { cursor: from } : {}),
              } as never,
            },
          }),
        );
        setItems((previous) => (from ? [...previous, ...data.items] : data.items));
        setCursor(data.next_cursor ?? null);
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 401) {
          expire();
          return;
        }
        const { key, values } = errorKey(caught);
        setError(t(key, values));
      } finally {
        setLoading(false);
      }
    },
    [applied, expire, t],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-xl font-bold text-brand-900">{t('admin.audit.title')}</h1>

      <Card
        title={t('admin.stats.filters')}
        actions={
          <div className="flex items-center gap-2">
            <Button tone="secondary" onClick={() => setApplied(EMPTY)}>
              {t('admin.inbox.filterReset')}
            </Button>
            <Button onClick={() => setApplied(draft)}>{t('admin.common.apply')}</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Field label={t('admin.audit.actorKind')}>
            <Select
              value={draft.actor_kind}
              onChange={(event) => setDraft({ ...draft, actor_kind: event.target.value })}
            >
              <option value="">{t('admin.common.all')}</option>
              {ACTOR_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`admin.auditActor.${kind}`)}
                </option>
              ))}
            </Select>
          </Field>

          {/*
            A CLOSED dropdown built from the domain's own list, not a text box.
            Typing an action string is guessing at an identifier; picking one is
            asking a question the log can answer.
          */}
          <Field label={t('admin.audit.action')}>
            <Select
              value={draft.action}
              onChange={(event) => setDraft({ ...draft, action: event.target.value })}
            >
              <option value="">{t('admin.common.all')}</option>
              {AUDIT_ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {t(actionKey(action))}
                </option>
              ))}
            </Select>
          </Field>

          {/* The subject is a REFERENCE, and the label says so — nobody should
              be trying a citizen's name here. */}
          <Field label={t('admin.audit.subjectId')} hint={t('admin.audit.subjectIdHint')}>
            <TextInput
              value={draft.subject_id}
              maxLength={64}
              placeholder="NV-20260812-A7C4"
              onChange={(event) => setDraft({ ...draft, subject_id: event.target.value })}
            />
          </Field>

          <Field label={t('admin.inbox.filterFrom')}>
            <TextInput
              type="date"
              value={draft.from}
              onChange={(event) => setDraft({ ...draft, from: event.target.value })}
            />
          </Field>
          <Field label={t('admin.inbox.filterTo')}>
            <TextInput
              type="date"
              value={draft.to}
              onChange={(event) => setDraft({ ...draft, to: event.target.value })}
            />
          </Field>
        </div>
      </Card>

      <ErrorBanner message={error} />

      <Card title={t('admin.audit.title')}>
        {loading && items.length === 0 ? <Spinner label={t('admin.common.loading')} /> : null}

        {!loading && items.length === 0 ? (
          <EmptyState message={t('admin.audit.empty')} />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>{t('admin.audit.at')}</Th>
                  <Th>{t('admin.audit.action')}</Th>
                  <Th>{t('admin.audit.actor')}</Th>
                  <Th>{t('admin.audit.subject')}</Th>
                  <Th>{t('admin.audit.detail')}</Th>
                </tr>
              }
            >
              {items.map((entry) => (
                <Tr key={entry.id}>
                  <Td className="whitespace-nowrap text-xs">{formatDateTime(entry.at)}</Td>
                  <Td>
                    <span className="flex flex-wrap items-center gap-2">
                      <ActionLabel action={entry.action} />
                      {/*
                        §14.6 — the reveal marker as its own badge. This is the
                        line an investigation looks for, and it must be visible
                        without expanding anything.
                      */}
                      {entry.revealed ? (
                        <Badge tone="danger">{t('admin.audit.revealed')}</Badge>
                      ) : null}
                    </span>
                  </Td>
                  <Td>
                    <span className="flex flex-col">
                      <span className="text-ink-900">
                        {entry.actor_name ?? t(`admin.auditActor.${entry.actor_kind}`)}
                      </span>
                      {entry.actor_name ? (
                        <span className="text-xs text-ink-500">
                          {t(`admin.auditActor.${entry.actor_kind}`)}
                        </span>
                      ) : null}
                    </span>
                  </Td>
                  <Td>
                    <SubjectCell type={entry.subject_type} id={entry.subject_id} />
                  </Td>
                  <Td>
                    <DetailCell detail={entry.detail} />
                  </Td>
                </Tr>
              ))}
            </Table>

            {cursor ? (
              <div className="pt-4">
                <Button tone="secondary" disabled={loading} onClick={() => void load(cursor)}>
                  {t('admin.inbox.loadMore')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * The action, labelled from the closed set — and rendered RAW when it is not in
 * it (§14.6).
 *
 * `translate()` already falls back to the key itself for a missing dictionary
 * entry, but that fallback would print the KEY, not the action. The membership
 * test is what makes the fallback print the ACTION instead, in a monospace face
 * so it reads as an identifier rather than as broken copy.
 */
/**
 * The dictionary key for an action string — with the DOTS FLATTENED to
 * underscores, which is load-bearing and not cosmetic.
 *
 * `t()` resolves a key by splitting on `.` and walking the nested JSON, so two
 * collisions have to be designed out rather than hoped away:
 *
 *  1. Action strings that are PREFIXES OF EACH OTHER — `admin.login` and
 *     `admin.login.failed` — would need `login` to be a STRING and an OBJECT in
 *     one dictionary. Not expressible in JSON; the label that lost would render
 *     as its own key. So the dots inside an action are flattened to underscores.
 *  2. The FAMILY cannot live under `admin.audit.action.*`, because
 *     `admin.audit.action` is itself a leaf — the column header "Action". Same
 *     string-and-object clash, one level up. So the family is a SIBLING
 *     namespace, `admin.auditAction.*`, alongside `admin.auditActor.*` and
 *     `admin.auditSubject.*`.
 *
 * `admin.login.failed` therefore becomes `admin.auditAction.admin_login_failed`:
 * one flat leaf per action, no collision possible.
 */
function actionKey(action: string): string {
  return `admin.auditAction.${action.replace(/\./g, '_')}`;
}

function ActionLabel({ action }: { action: string }): React.JSX.Element {
  const { t } = useI18n();
  const known = (AUDIT_ACTIONS as readonly string[]).includes(action);
  if (known) return <span className="font-semibold text-ink-900">{t(actionKey(action))}</span>;
  // §14.6 — an action absent from the closed list renders its RAW string, in a
  // monospace face so it reads as an identifier rather than as broken copy.
  return <code className="rounded-chip bg-surface-100 px-2 py-0.5 text-xs text-ink-700">{action}</code>;
}

/** Links a subject to the record it names, when the panel has a screen for it. */
function SubjectCell({ type, id }: { type: string; id: string | undefined }): React.JSX.Element {
  const { t } = useI18n();
  if (!id) return <span className="text-ink-400">{t(`admin.auditSubject.${type}`)}</span>;

  const body =
    type === 'appellant' ? (
      <Link to={`/appellants/${id}`} className="font-semibold text-brand-700 hover:underline">
        {t('admin.audit.appellantSubject')}
      </Link>
    ) : type === 'murojaat' && isAnyReference(id) ? (
      // The inbox's `q` filter is reference-only (§7.2), which is exactly what
      // this subject is — so the link lands on the appeal without inventing a
      // lookup route.
      <Link
        to={`/murojaats?q=${encodeURIComponent(id)}`}
        className="font-semibold tracking-ref text-brand-700 hover:underline"
      >
        {id}
      </Link>
    ) : (
      <span className="text-ink-700">{id}</span>
    );

  return (
    <span className="flex flex-col">
      {body}
      <span className="text-xs text-ink-500">{t(`admin.auditSubject.${type}`)}</span>
    </span>
  );
}

/**
 * `detail` as a key/value list.
 *
 * Rendered whole and unfiltered ON PURPOSE: the object is already PII-free by
 * the §10.4 write-time allow-list in `services/api/src/lib/audit.ts`, which
 * DROPS any key outside a closed set. A second filter here would be a second
 * place to maintain the same rule, and the two would drift — with this one
 * silently hiding a field the server had decided was safe to record.
 */
function DetailCell({ detail }: { detail: Record<string, unknown> }): React.JSX.Element {
  const entries = Object.entries(detail).filter(([key]) => key !== 'revealed');
  if (entries.length === 0) return <span className="text-ink-400">—</span>;
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-600">
      {entries.map(([key, value]) => (
        <li key={key}>
          <span className="text-ink-400">{key}:</span> {String(value)}
        </li>
      ))}
    </ul>
  );
}
